import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { BsArrowRight } from "react-icons/bs";
import { MdDragIndicator } from "react-icons/md";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AmbikaVenderApi } from "./config";
import Swal from "sweetalert2";
import { FiRefreshCcw } from "react-icons/fi";


const SwipeToLoadButton = ({ onSwipe, text = "Swipe to Load" }) => {
  const [left, setLeft] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [unlocked, setUnlocked] = React.useState(false);
  const sliderRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const handleDrag = (clientX) => {
    if (unlocked || !isDragging) return;
    const container = containerRef.current;
    const slider = sliderRef.current;
    if (!container || !slider) return;

    const containerRect = container.getBoundingClientRect();
    const maxLeft = containerRect.width - slider.offsetWidth - 2;
    let newLeft = clientX - containerRect.left - slider.offsetWidth / 2;

    if (newLeft < 0) newLeft = 0;
    if (newLeft >= maxLeft) {
      newLeft = maxLeft;
      setUnlocked(true);
      onSwipe();
    }
    setLeft(newLeft);
  };

  const onMouseMove = (e) => handleDrag(e.clientX);
  const onTouchMove = (e) => handleDrag(e.touches[0].clientX);
  
  const stopDrag = () => {
    setIsDragging(false);
    if (!unlocked) setLeft(0);
  };

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stopDrag);
      window.addEventListener('touchmove', onTouchMove);
      window.addEventListener('touchend', stopDrag);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDrag);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [isDragging, unlocked]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '180px',
        height: '34px',
        backgroundColor: '#e8f5e9',
        borderRadius: '17px',
        border: '1px solid #198754',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: unlocked ? 'default' : 'pointer'
      }}
    >
      <span style={{ color: '#198754', fontSize: '11px', fontWeight: 'bold', marginLeft: '25px', transition: 'opacity 0.3s', opacity: unlocked ? 0 : 1 }}>
        {text}
      </span>
      <div
        ref={sliderRef}
        onMouseDown={(e) => { e.stopPropagation(); if(!unlocked) setIsDragging(true); }}
        onTouchStart={(e) => { e.stopPropagation(); if(!unlocked) setIsDragging(true); }}
        style={{
          position: 'absolute',
          left: `${left}px`,
          top: '2px',
          width: '28px',
          height: '28px',
          backgroundColor: '#198754',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          transition: isDragging ? 'none' : 'left 0.3s ease',
          zIndex: 2,
        }}
      >
        <BsArrowRight size={16} />
      </div>
      {unlocked && (
        <span style={{ position: 'absolute', color: '#198754', fontSize: '11px', fontWeight: 'bold' }}>
          Loaded
        </span>
      )}
    </div>
  );
};

const SwipeToRemoveButton = ({ onSwipe, text = "Swipe to Remove" }) => {
  const [right, setRight] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [unlocked, setUnlocked] = React.useState(false);
  const sliderRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const handleDrag = (clientX) => {
    if (unlocked || !isDragging) return;
    const container = containerRef.current;
    const slider = sliderRef.current;
    if (!container || !slider) return;

    const containerRect = container.getBoundingClientRect();
    const maxRight = containerRect.width - slider.offsetWidth - 2;
    let newRight = (containerRect.right - clientX) - slider.offsetWidth / 2;

    if (newRight < 0) newRight = 0;
    if (newRight >= maxRight) {
      newRight = maxRight;
      setUnlocked(true);
      onSwipe();
    }
    setRight(newRight);
  };

  const onMouseMove = (e) => handleDrag(e.clientX);
  const onTouchMove = (e) => handleDrag(e.touches[0].clientX);
  
  const stopDrag = () => {
    setIsDragging(false);
    if (!unlocked) setRight(0);
  };

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stopDrag);
      window.addEventListener('touchmove', onTouchMove);
      window.addEventListener('touchend', stopDrag);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDrag);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDrag);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stopDrag);
    };
  }, [isDragging, unlocked]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '180px',
        height: '34px',
        backgroundColor: '#f8d7da',
        borderRadius: '17px',
        border: '1px solid #dc3545',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: unlocked ? 'default' : 'pointer'
      }}
    >
      <span style={{ color: '#dc3545', fontSize: '11px', fontWeight: 'bold', marginRight: '25px', transition: 'opacity 0.3s', opacity: unlocked ? 0 : 1 }}>
        {text}
      </span>
      <div
        ref={sliderRef}
        onMouseDown={(e) => { e.stopPropagation(); if(!unlocked) setIsDragging(true); }}
        onTouchStart={(e) => { e.stopPropagation(); if(!unlocked) setIsDragging(true); }}
        style={{
          position: 'absolute',
          right: `${right}px`,
          top: '2px',
          width: '28px',
          height: '28px',
          backgroundColor: '#dc3545',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          transition: isDragging ? 'none' : 'right 0.3s ease',
          zIndex: 2,
        }}
      >
        <BsArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
      </div>
      {unlocked && (
        <span style={{ position: 'absolute', color: '#dc3545', fontSize: '11px', fontWeight: 'bold' }}>
          Removed
        </span>
      )}
    </div>
  );
};

const LoadtruckItems = () => {
  const [options, setOptions] = useState(true);
  const [route, setRoute] = useState();
  const [vehicle, setVehicle] = useState();
  const [loading, setLoading] = useState(false);
  const [routeVehicle, setRouteVehicle] = useState();

  const [pandingTruckProductList, setPandingTruckProductList] = useState();
  const [urgentrouteProductList, setUrgentRouteProductList] = useState();
  const [tempLoadProductList, setTempLoadProductList] = useState();

  const [tempLoadDetails, settempLoadDetails] = useState();

  const ambikaDriverToken = localStorage.getItem("ambikaDriverToken");
  const ambikaDriverId = localStorage.getItem("ambikaDriverId");
  const navigate = useNavigate();

  // useEffect(() => {
  //     setOptions(true)
  // }, [])

  useEffect(() => {
    axios
      .get(`${AmbikaVenderApi}/driver/allRouteList`, {
        headers: { Authorization: `Bearer ${ambikaDriverToken}` },
      })
      .then((res) => {
        setRoute(res?.data?.data);
      })
      .catch((err) => {
        // console.log(err);
      });

    axios
      .get(`${AmbikaVenderApi}/driver/allVehicleList`, {
        headers: { Authorization: `Bearer ${ambikaDriverToken}` },
      })
      .then((res) => {
        setVehicle(res?.data?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [ambikaDriverToken, ambikaDriverId]);
  // console.log(route, vehicle);

  const handelSelectRoutVehicle = (e) => {
    const { name, value } = e.target;
    setRouteVehicle({ ...routeVehicle, [name]: value });
  };

  // const handelCallLoadedItems =   () => {
  //     const details = {
  //         ...routeVehicle,
  //         UserId: ambikaDriverId,
  //     }
  //     // console.log(details);
  //     if (routeVehicle?.RouteId != undefined && routeVehicle?.RouteId != ''
  //     ) {

  //         axios.post(`${AmbikaVenderApi}/driver/pendingTruckLoadProductList`, details, { headers: { Authorization: `Bearer ${ambikaDriverToken}` } })
  //             .then((res) => {
  //                 console.log(res?.data?.data)
  //                 setPandingTruckProductList(res?.data?.data)
  //                 if(res?.data?.data?.length > 0){
  //                     setOptions(false)
  //                 }
  //                 //  setRoute(res?.data?.data);
  //             })
  //             .catch((err) => {
  //                 Swal.fire({
  //                     text: `${err?.response?.data?.message}`,
  //                 });
  //             })

  //         axios.post(`${AmbikaVenderApi}/driver/urgentRouteProductList`, details, { headers: { Authorization: `Bearer ${ambikaDriverToken}` } })
  //             .then((res) => {
  //                 console.log(res?.data?.data)
  //                 //  setRoute(res?.data?.data);
  //                 setUrgentRouteProductList(res?.data?.data)
  //                 if(res?.data?.data?.length > 0){
  //                     setOptions(false)
  //                 }

  //             })
  //             .catch((err) => {
  //                 Swal.fire({
  //                     text: `${err?.response?.data?.message}`,
  //                 });
  //             })

  //         axios.post(`${AmbikaVenderApi}/driver/tempLoadProductList`, details, { headers: { Authorization: `Bearer ${ambikaDriverToken}` } })
  //             .then((res) => {
  //                 console.log(res?.data?.data)
  //                 //  setRoute(res?.data?.data);
  //                 setTempLoadProductList(res?.data?.data)
  //                 if(res?.data?.data?.length > 0){
  //                     setOptions(false)
  //                 }

  //             })
  //             .catch((err) => {
  //                 Swal.fire({
  //                     text: `${err?.response?.data?.message}`,
  //                 });
  //             })
  //     } else {
  //         Swal.fire({
  //             text: `Route ID & Vehicle ID both are required`,
  //         });
  //     }

  // }

  const handelCallLoadedItems = async () => {
    const details = {
      ...routeVehicle,
      UserId: ambikaDriverId,
    };
    // console.log(details);
    if (routeVehicle?.RouteId != undefined && routeVehicle?.RouteId != "") {
      try {
        const url1 = `${AmbikaVenderApi}/driver/pendingTruckLoadProductList`;
        const url2 = `${AmbikaVenderApi}/driver/urgentRouteProductList`;
        const url3 = `${AmbikaVenderApi}/driver/tempLoadProductList`;

        const [response1, response2, response3] = await Promise.all([
          fetch(url1, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${ambikaDriverToken}`,
            },
            body: JSON.stringify(details),
          }),
          fetch(url2, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${ambikaDriverToken}`,
            },
            body: JSON.stringify(details),
          }),
          fetch(url3, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${ambikaDriverToken}`,
            },
            body: JSON.stringify(details),
          }),
        ]);

        const result1 = await response1.json();
        const result2 = await response2.json();
        const result3 = await response3.json();

        if (
          result1?.data?.length > 0 ||
          result2?.data?.length > 0 ||
          result3?.data?.length > 0
        ) {
          setOptions(false);
          setPandingTruckProductList(result1?.data);
          setUrgentRouteProductList(result2?.data);
          setTempLoadProductList(result3?.data);
        }
      } catch (err) {
        console.error("Error posting data:", err);
      }
    } else {
      Swal.fire({
        text: `Route ID & Vehicle ID both are required`,
      });
    }
  };

  
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
      if (destination.droppableId === 'tempLoadList') {
        const parts = result.draggableId.split('-');
        if (parts[0] === 'truck' && parts[1] === 'item') {
          const orderId = parts[2];
          const productId = parts[3];
          const orderPId = parts[4];
          const productSize = parts[5];
          const productQty = parts[6];

          const product = {
           product_id: productId,
           orderProduct_id: orderPId,
           size_id: productSize,
           product_qty: productQty
          };
          temploadedTruck(orderId, product);
        }
      } else if (destination.droppableId === 'truckList') {
        const parts = result.draggableId.split('-');
        if (parts[0] === 'temp' && parts[1] === 'item') {
          const tempId = parts[2];
          tempRemoveTruck(tempId);
        }
      }
    }
  };

  const temploadedTruck = (ordeerId, product) => {
    const tampLoaddetails = {
      UserId: ambikaDriverId,
      RouteId: routeVehicle?.RouteId,
      VehicleId: routeVehicle?.VehicleId,
      OrderId: ordeerId,
      OrderPId: product?.orderProduct_id,
      ProductId: product?.product_id,
      ProductSize: product?.size_id,
      ProductQty: product?.product_qty,
    };

    axios
      .post(
        `${AmbikaVenderApi}/driver/addUpdateTempLoadTruck`,
        tampLoaddetails,
        { headers: { Authorization: `Bearer ${ambikaDriverToken}` } }
      )
      .then((res) => {
        // console.log(res?.data?.data)
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          text: `${res?.data?.message}`,
          showConfirmButton: false,
          timer: 2000
        });
        handelCallLoadedItems();
      })
      .catch((err) => {
        Swal.fire({
          text: `${err?.response?.data?.message}`,
        });
      });
  };

  const tempRemoveTruck = (e) => {
    axios
      .post(
        `${AmbikaVenderApi}/driver/removeTempLoadProduct`,
        { TempId: e },
        { headers: { Authorization: `Bearer ${ambikaDriverToken}` } }
      )
      .then((res) => {
        // console.log(res?.data?.data)
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          text: `${res?.data?.message}`,
          showConfirmButton: false,
          timer: 2000
        });
        handelCallLoadedItems();
      })
      .catch((err) => {
        Swal.fire({
          text: `${err?.response?.data?.message}`,
        });
      });
  };

  const foodLoadedOnTruck = async () => {
    const details = {
      ...routeVehicle,
      UserId: ambikaDriverId,
    };
    // console.log(details);
    if (routeVehicle?.RouteId != undefined && routeVehicle?.RouteId != "") {
      try {
        const url1 = `${AmbikaVenderApi}/driver/foodLoadedOnTruck`;
        const [response1, response2] = await Promise.all([
          fetch(url1, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${ambikaDriverToken}`,
            },
            body: JSON.stringify(details),
          }),
        ]);

        const result1 = await response1.json();
        if (!response1.ok) {
          throw new Error(result1?.message);
        }
        if (result1?.success) {
          Swal.fire({
            text: result1?.message,
          });
          setOptions(true);
        }
      } catch (err) {
        Swal.fire({
          text: err,
        });
        setOptions(true);
      }
    } else {
      Swal.fire({
        text: `Route ID & Vehicle ID both are required`,
      });
    }
  };

  const refreshTruckProductList = async () => {
    if (!routeVehicle?.RouteId) return;

    setLoading(true);
    try {
      const details = {
        ...routeVehicle,
        UserId: ambikaDriverId,
      };

      const [response1, response2] = await Promise.all([
        axios.post(
          `${AmbikaVenderApi}/driver/pendingTruckLoadProductList`,
          details,
          {
            headers: { Authorization: `Bearer ${ambikaDriverToken}` },
          }
        ),
        axios.post(
          `${AmbikaVenderApi}/driver/urgentRouteProductList`,
          details,
          {
            headers: { Authorization: `Bearer ${ambikaDriverToken}` },
          }
        ),
      ]);

      setPandingTruckProductList(response1?.data?.data);
      setUrgentRouteProductList(response2?.data?.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        text:
          err?.response?.data?.message ||
          "Failed to refresh Truck Product List",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshTempLoadProductList = async () => {
    if (!routeVehicle?.RouteId) return;

    setLoading(true);
    try {
      const details = {
        ...routeVehicle,
        UserId: ambikaDriverId,
      };

      const response = await axios.post(
        `${AmbikaVenderApi}/driver/tempLoadProductList`,
        details,
        {
          headers: { Authorization: `Bearer ${ambikaDriverToken}` },
        }
      );

      setTempLoadProductList(response?.data?.data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        text:
          err?.response?.data?.message ||
          "Failed to refresh Temp Load Product List",
      });
    } finally {
      setLoading(false);
    }
  };

  let truckItemIndex = 0;
  let tempItemIndex = 0;
  return (
    <div>
      {options && (
        <div
          class="d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.3)",
            width: "100%",
            height: "100vh",
            position: "fixed",
            top: "0",
          }}
        >
          <div className="col-lg-6 col-md-8 col-11 m-auto ">
            <div class="mt-5 boxSadow2 bg-white rounded-3">
              <br />
              <p class="fs-5 fw-bold ">Load truck Item</p>
              <div class="p-2 text-start col-11 m-auto mt-3">
                <label htmlFor="route">Select Route</label>
                <select
                  class="form-select"
                  id="route"
                  name="RouteId"
                  onChange={handelSelectRoutVehicle}
                >
                  <option selected></option>
                  {route &&
                    route?.map((route) => {
                      return <option value={route?.id}>{route?.name}</option>;
                    })}
                </select>

                <label htmlFor="vehicle" class="mt-2">
                  Select Vehicle
                </label>
                <select
                  id="vehicle"
                  class="form-select"
                  name="VehicleId"
                  onChange={handelSelectRoutVehicle}
                >
                  <option selected></option>
                  {vehicle?.map((vehicle) => (
                    <option
                      key={vehicle.id}
                      value={vehicle.id}
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <span style={{ fontWeight: "600" }}>{vehicle.name}</span>
                      <span
                        style={{
                          color: "#6c757d",
                          marginLeft: "8px",
                          fontSize: "0.9em",
                        }}
                      >
                        ({vehicle.vehicle_no})
                      </span>
                    </option>
                  ))}
                </select>
                <button
                  class="form-control btn btn-success mt-3 rounded-5"
                  onClick={handelCallLoadedItems}
                >
                  Next{" "}
                </button>
                <button
                  class="form-control btn btn-outline-success my-3 rounded-5"
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard{" "}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(pandingTruckProductList ||
        urgentrouteProductList ||
        tempLoadProductList) && (
        <>
          <DragDropContext onDragEnd={onDragEnd}>
          <div class="my-3">
            <div class="col-12 m-auto row " style={{ padding: "1rem" }}>
              <div class="col-lg-6 col-md-6 mt-3">
                <Droppable droppableId="truckList">
                {(provided, snapshot) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps}
                  style={{
                    backgroundColor: snapshot.isDraggingOver ? "#f8f9fa" : "transparent",
                    minHeight: "150px",
                    padding: "10px",
                    borderRadius: "10px",
                    border: snapshot.isDraggingOver ? "2px dashed #0d6efd" : "2px dashed transparent",
                    transition: "all 0.3s ease"
                  }}
                >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                  }}
                >
                  <p class="text-start fw-bold" style={{ margin: 0 }}>
                    Truck Product List
                  </p>
                  <button
                    onClick={refreshTruckProductList}
                    disabled={loading}
                    style={{
                      backgroundColor: "#198754",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: loading ? "not-allowed" : "pointer",
                      opacity: loading ? 0.7 : 1,
                      transition: "all 0.2s ease",
                      padding: 0,
                    }}
                    title="Refresh list"
                  >
                    {loading ? (
                      <div
                        class="spinner-border spinner-border-sm text-light"
                        role="status"
                      >
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <FiRefreshCcw style={{ fontSize: "16px" }} />
                    )}
                  </button>
                </div>
                {/* Rest of your content */}
                {urgentrouteProductList
                  ?.filter((el) => el?.product_detail?.length > 0)
                  .map((el) => {
                    return (
                      <div class="boxSadow1 rounded-4 bg-white p-2 mt-2">
                        <div class="d-flex align-items-center justify-content-between mt-1 fw-semibold">
                          <p style={{ fontSize: "15px", margin: 5 }}>
                            {el?.pickup_date}
                          </p>
                          <p style={{ fontSize: "15px", margin: 5 }}>
                            # {el?.order_id}
                          </p>
                        </div>
                        <div>
                          {el?.product_detail?.map((e) => {
                            return (
                              <Draggable key={`truck-item-${el?.order_id}-${e?.product_id}-${e?.orderProduct_id}-${e?.size_id}-${e?.product_qty}`} draggableId={`truck-item-${el?.order_id}-${e?.product_id}-${e?.orderProduct_id}-${e?.size_id}-${e?.product_qty}`} index={truckItemIndex++}>
                              {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                class="mt-2 rounded-3"
                                style={{ background: "#f3f3f3", ...provided.draggableProps.style }}
                              >
                                <div className="d-flex align-items-center justify-content-between p-2">
                                  <div class="text-start">
                                    <p
                                      class="fw-semibold"
                                      style={{
                                        fontSize: "15px",
                                        margin: "5px 0px",
                                      }}
                                    >
                                      {e?.productName}
                                    </p>
                                    <div className="d-flex gap-2 ">
                                      <div>
                                        <div className="d-flex gap-1">
                                          {e?.is_parcel ? (
                                            <div
                                              class="d-flex align-items-center justify-content-center fw-bold"
                                              style={
                                                e?.is_parcel
                                                  ? {
                                                      background: "#FF6969",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "white",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid #FF6969",
                                                    }
                                                  : {
                                                      background: "#f3f3f3",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "gray",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid gray",
                                                    }
                                              }
                                            >
                                              P
                                            </div>
                                          ) : (
                                            <></>
                                          )}
                                          {e?.is_urgent ? (
                                            <div
                                              className="d-flex align-items-center justify-content-center fw-bold"
                                              style={
                                                e?.is_urgent
                                                  ? {
                                                      background: "#FF6969",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "white",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid #FF6969",
                                                    }
                                                  : {
                                                      background: "#f3f3f3",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "gray",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid gray",
                                                    }
                                              }
                                            >
                                              U
                                            </div>
                                          ) : (
                                            <></>
                                          )}
                                          <p
                                            style={{
                                              fontSize: "13px",
                                              margin: 0,
                                            }}
                                          >
                                            {e?.size_name} X {e?.product_qty}
                                          </p>
                                        </div>
                                        <div class="">
                                          {e?.message_option && (
                                            <div class="mb-1">
                                              <span className="text-muted small">
                                                Message:{" "}
                                              </span>
                                              <span className="small">
                                                {e?.message_option}
                                              </span>
                                            </div>
                                          )}
                                          <div className="d-flex gap-2 align-items-center">
                                            {e?.shap_option && (
                                              <div class="mb-1">
                                                <span className="text-muted small">
                                                  Shape:
                                                </span>
                                                <span className="small">
                                                  {e?.shap_option}
                                                </span>
                                              </div>
                                            )}
                                            {e?.eggless_option && (
                                              <label
                                                className="form-check-label upCheckBox"
                                                style={{
                                                  backgroundColor: "#FF6969",
                                                  color: "white",
                                                  border: "1px solid #FF6969 ",
                                                  width: "auto",
                                                  borderRadius: "15px",
                                                  padding: "5px 5px",
                                                  fontSize: "12px",
                                                  margin: "4px 0px",
                                                }}
                                              >
                                                Less cream
                                              </label>
                                            )}
                                          </div>
                                        </div>
                                        {/* )} */}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <SwipeToLoadButton onSwipe={() => temploadedTruck(el?.order_id, e)} />
                                    <div {...provided.dragHandleProps} style={{ cursor: "grab", padding: "5px" }}>
                                      <MdDragIndicator size={24} color="#6c757d" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              )}
                              </Draggable>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                {pandingTruckProductList
                  ?.filter((el) => el?.product_detail?.length > 0)
                  .map((el) => {
                    return (
                      <div class="boxSadow1 rounded-4 bg-white p-2 mt-2">
                        <div class="d-flex align-items-center justify-content-between mt-1 fw-semibold">
                          <p style={{ fontSize: "15px", margin: 5 }}>
                            {el?.pickup_date}
                          </p>
                          <p style={{ fontSize: "15px", margin: 5 }}>
                            # {el?.order_id}
                          </p>
                        </div>
                        <div>
                          {el?.product_detail?.map((e) => {
                            return (
                              <Draggable key={`truck-item-${el?.order_id}-${e?.product_id}-${e?.orderProduct_id}-${e?.size_id}-${e?.product_qty}`} draggableId={`truck-item-${el?.order_id}-${e?.product_id}-${e?.orderProduct_id}-${e?.size_id}-${e?.product_qty}`} index={truckItemIndex++}>
                              {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                class="mt-2 rounded-3"
                                style={{ background: "#f3f3f3", ...provided.draggableProps.style }}
                              >
                                <div className="d-flex align-items-center justify-content-between p-2">
                                  <div class="text-start">
                                    <p
                                      class="fw-semibold"
                                      style={{
                                        fontSize: "15px",
                                        margin: "5px 0px",
                                      }}
                                    >
                                      {e?.productName}
                                    </p>
                                    <div className="d-flex gap-2 ">
                                      <div>
                                        <div className="d-flex gap-1">
                                          {e?.is_parcel ? (
                                            <div
                                              class="d-flex align-items-center justify-content-center fw-bold"
                                              style={
                                                e?.is_parcel
                                                  ? {
                                                      background: "#FF6969",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "white",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid #FF6969",
                                                    }
                                                  : {
                                                      background: "#f3f3f3",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "gray",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid gray",
                                                    }
                                              }
                                            >
                                              P
                                            </div>
                                          ) : (
                                            <></>
                                          )}
                                          {e?.is_urgent ? (
                                            <div
                                              className="d-flex align-items-center justify-content-center fw-bold"
                                              style={
                                                e?.is_urgent
                                                  ? {
                                                      background: "#FF6969",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "white",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid #FF6969",
                                                    }
                                                  : {
                                                      background: "#f3f3f3",
                                                      borderRadius: "50%",
                                                      fontSize: "12px",
                                                      color: "gray",
                                                      width: "20px",
                                                      height: "20px",
                                                      border:
                                                        "0.5px solid gray",
                                                    }
                                              }
                                            >
                                              U
                                            </div>
                                          ) : (
                                            <></>
                                          )}
                                          <p
                                            style={{
                                              fontSize: "13px",
                                              margin: 0,
                                            }}
                                          >
                                            {e?.size_name} X {e?.product_qty}
                                          </p>
                                        </div>
                                        <div class="">
                                          {e?.message_option && (
                                            <div class="mb-1">
                                              <span className="text-muted small">
                                                Message:{" "}
                                              </span>
                                              <span className="small">
                                                {e?.message_option}
                                              </span>
                                            </div>
                                          )}
                                          <div className="d-flex gap-2 align-items-center">
                                            {e?.shap_option && (
                                              <div class="mb-1">
                                                <span className="text-muted small">
                                                  Shape:
                                                </span>
                                                <span className="small">
                                                  {e?.shap_option}
                                                </span>
                                              </div>
                                            )}
                                            {e?.eggless_option && (
                                              <label
                                                className="form-check-label upCheckBox"
                                                style={{
                                                  backgroundColor: "#FF6969",
                                                  color: "white",
                                                  border: "1px solid #FF6969 ",
                                                  width: "auto",
                                                  borderRadius: "15px",
                                                  padding: "5px 5px",
                                                  fontSize: "12px",
                                                  margin: "4px 0px",
                                                }}
                                              >
                                                Less cream
                                              </label>
                                            )}
                                          </div>
                                        </div>
                                        {/* )} */}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <SwipeToLoadButton onSwipe={() => temploadedTruck(el?.order_id, e)} />
                                    <div {...provided.dragHandleProps} style={{ cursor: "grab", padding: "5px" }}>
                                      <MdDragIndicator size={24} color="#6c757d" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              )}
                              </Draggable>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                {provided.placeholder}
                </div>
                )}
                </Droppable>
              </div>

              <div class="col-lg-6 col-md-6 mt-3">
                  <Droppable droppableId="tempLoadList">
                  {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    style={{
                      backgroundColor: snapshot.isDraggingOver ? "#e8f5e9" : "transparent",
                      minHeight: "150px",
                      padding: "10px",
                      borderRadius: "10px",
                      border: snapshot.isDraggingOver ? "2px dashed #198754" : "2px dashed transparent",
                      transition: "all 0.3s ease"
                    }}
                  >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                    }}
                  >
                    <p class="text-start fw-bold" style={{ margin: 0 }}>
                      Temp Load Product List
                    </p>
                    <button
                      onClick={refreshTempLoadProductList}
                      disabled={loading}
                      style={{
                        backgroundColor: "#198754",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.7 : 1,
                        transition: "all 0.2s ease",
                        padding: 0,
                      }}
                      title="Refresh list"
                    >
                      {loading ? (
                        <div
                          class="spinner-border spinner-border-sm text-light"
                          role="status"
                        >
                          <span class="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <FiRefreshCcw style={{ fontSize: "16px" }} />
                      )}
                    </button>
                  </div>
                  {tempLoadProductList?.map((el) => {
                    return (
                      <Draggable key={`temp-item-${el?.id}`} draggableId={`temp-item-${el?.id}`} index={tempItemIndex++}>
                      {(provided) => (
                      <div 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        class="boxSadow1 rounded-4 bg-white p-2 mt-2"
                        style={{...provided.draggableProps.style}}
                      >
                        <div
                          class="mt-2 rounded-3"
                          style={{ background: "#f3f3f3" }}
                        >
                          <div className="d-flex align-items-center justify-content-between p-2">
                            <div class="text-start ">
                              <p
                                class="fw-semibold"
                                style={{ fontSize: "14px" }}
                              >
                                {" "}
                                {el?.productName}
                              </p>

                              <div className="d-flex gap-2 ">
                                <div>
                                  <div className="d-flex gap-1">
                                    {el?.is_parcel ? (
                                      <div
                                        class="d-flex align-items-center justify-content-center fw-bold"
                                        style={
                                          el?.is_parcel
                                            ? {
                                                background: "#FF6969",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                border: "0.5px solid #FF6969",
                                              }
                                            : {
                                                background: "#f3f3f3",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                color: "gray",
                                                width: "20px",
                                                height: "20px",
                                                border: "0.5px solid gray",
                                              }
                                        }
                                      >
                                        P
                                      </div>
                                    ) : (
                                      <></>
                                    )}
                                    {el?.is_urgent ? (
                                      <div
                                        className="d-flex align-items-center justify-content-center fw-bold"
                                        style={
                                          el?.is_urgent
                                            ? {
                                                background: "#FF6969",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                border: "0.5px solid #FF6969",
                                              }
                                            : {
                                                background: "#f3f3f3",
                                                borderRadius: "50%",
                                                fontSize: "12px",
                                                color: "gray",
                                                width: "20px",
                                                height: "20px",
                                                border: "0.5px solid gray",
                                              }
                                        }
                                      >
                                        U
                                      </div>
                                    ) : (
                                      <></>
                                    )}
                                    <p
                                      style={{
                                        fontSize: "13px",
                                        margin: 0,
                                      }}
                                    >
                                      {el?.size_name} X {el?.product_qty}
                                    </p>
                                  </div>
                                  <div class="">
                                    {el?.order_message && (
                                      <div class="mb-1">
                                        <span className="text-muted small">
                                          Message:{" "}
                                        </span>
                                        <span className="small">
                                          {el?.order_message}
                                        </span>
                                      </div>
                                    )}
                                    <div className="d-flex gap-2 align-items-center">
                                      {el?.shap_option && (
                                        <div class="mb-1">
                                          <span className="text-muted small">
                                            Shape:
                                          </span>
                                          <span className="small">
                                            {el?.shap_option}
                                          </span>
                                        </div>
                                      )}
                                      {el?.eggless_option && (
                                        <label
                                          className="form-check-label upCheckBox"
                                          style={{
                                            backgroundColor: "#FF6969",
                                            color: "white",
                                            border: "1px solid #FF6969 ",
                                            width: "auto",
                                            borderRadius: "15px",
                                            padding: "5px 5px",
                                            fontSize: "12px",
                                            margin: "4px 0px",
                                          }}
                                        >
                                          Less cream
                                        </label>
                                      )}
                                    </div>
                                  </div>
                                  {/* )} */}
                                </div>
                              </div>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-2 justify-content-between">
                              <div className="d-flex align-items-center gap-2">
                                <p style={{ fontSize: "14px", margin: 0 }} className="fw-bold">
                                  #{el?.oid}
                                </p>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <div {...provided.dragHandleProps} style={{ cursor: "grab", padding: "5px", backgroundColor: "#f8f9fa", borderRadius: "5px" }}>
                                  <MdDragIndicator size={24} color="#6c757d" />
                                </div>
                                <SwipeToRemoveButton onSwipe={() => tempRemoveTruck(el?.id)} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  </div>
                  )}
                  </Droppable>
                </div>
            </div>
          </div>
          </DragDropContext>

          <button
            class="btn btn-success my-3 rounded-5 px-4"
            onClick={foodLoadedOnTruck}
          >
            GenerateÂ TAXÂ Invoice
          </button>
        </>
      )}
    </div>
  );
};

export default LoadtruckItems;
